import React, { useRef } from 'react';
import {
    arrayOf,
    bool,
    node,
    object,
    oneOfType,
    shape,
    string
} from 'prop-types';
import clsx from 'clsx';
import { isPlainObject } from 'lodash';

import { anyValue } from '../../helpers/objects';
import Checkbox from '@material-ui/core/Checkbox';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormHelperText from '@material-ui/core/FormHelperText';
import RootRef from '@material-ui/core/RootRef';

import defaultClasses from './CheckboxGroup.module.css';

/**
 * CheckboxGroup can handle multiple value types:
 *
 * *  An array of enumerables:
 *
 *      ['Mayonnaise', 'Mustard', 'Lettuce']
 *
 * *  A object containing boolean flags:
 *
 *      { mayo: true, mustard: true, lettuce: false }
 *
 * When using an array of enumerables as a value, the options that define each
 * checkbox can be provided with an array:
 *
 *     ['Mayonnaise', 'Mustard', 'Lettice', 'Tomato', 'Onion']
 *
 * When a user toggles one of these options, the label will be added or removed
 * from the array of values.
 *
 * When using an object containing boolean flags, the options that define each
 * checkbox need to be provided with an array of objects so that the correct
 * flag can be found in the value and toggled:
 *
 *      [
 *        { key: 'mayo', label: 'Mayonnaise' },
 *        { key: 'mustard', label: 'Mustard' },
 *        { key: 'lettuce', label: 'Lettuce' },
 *      ]
 *
 * When a user toggles the "Mayonnaise" option, the key `mayo` of the value
 * object would be toggled.
 *
 * Providing an object of options also works when the value is an array of
 * enumerables
 */
const CheckboxGroup = ({
    CheckboxProps,
    disabled,
    error,
    errors,
    FormGroupProps,
    helperText,
    hidden,
    inputProps,
    label,
    name,
    options,
    onBlur,
    onChange,
    required,
    touched,
    value
}) => {
    const formGroup = useRef();
    if (hidden) {
        return null;
    }
    const isTouched = isPlainObject(touched) ? anyValue(touched, true) : touched;
    const isEnumValue = Array.isArray(value);
    error || (error = isTouched && Boolean(errors));
    helperText = (isTouched && errors) || helperText;

    /**
     * When a user tabs through the options in a Checkbox Group that is part of
     * a form that is configured to validate on blur, the form's validations
     * will run each time the user focuses on the next checkbox. This can be a
     * bit confusing since the user may not be "done" with that particular
     * section of the form.
     *
     * If we treat the Checkbox Group as a holistic field by only calling the
     * `onBlur` callback when the user focuses on an element _outside_ of the
     * group, then the form's validations will run at a time that makes sense
     * to the user. (perceptually when the user is "done" with this particular
     * "field").
     */
    function handleBlur(event) {
        if (!formGroup.current || !event.relatedTarget) {
            onBlur(event);
            return;
        }
        if (!formGroup.current.contains(event.relatedTarget)) {
            onBlur(event);
            return;
        }
    }

    return (
        <FormControl
            component="fieldset"
            error={error}
            required={required}
        >
            <FormLabel
                classes={{
                    error: defaultClasses.labelError,
                    root:  defaultClasses.label
                }}
                component="legend"
                focused={false}
            >
                {label}
            </FormLabel>
            <RootRef rootRef={formGroup}>
                <FormGroup
                    {...FormGroupProps}
                    classes={{
                        ...FormGroupProps.classes,
                        root: clsx(
                            defaultClasses.formGroup,
                            FormGroupProps.classes.root
                        )
                    }}
                    onBlur={handleBlur}
                >
                    {options.map(optionValue => {
                        let isChecked = false;
                        let label = optionValue.label || optionValue;
                        let checkboxName =
                      [name, optionValue.key]
                          .filter(x => x)
                          .join('.');
                        let checkboxValue;
                        if (isEnumValue) {
                            checkboxValue = optionValue.value || optionValue.label || optionValue || '';
                            isChecked = value.includes(checkboxValue);
                        } else {
                            isChecked = value[optionValue.key];
                            checkboxValue = String(isChecked);
                        }
                        return (
                            <FormControlLabel
                                classes={{
                                    label: defaultClasses.checkboxLabel
                                }}
                                control={
                                    <Checkbox
                                        checked={isChecked}
                                        classes={{
                                            ...CheckboxProps.classes,
                                            root: clsx(
                                                defaultClasses.checkbox,
                                                CheckboxProps.classes.checkbox
                                            )
                                        }}
                                        disabled={disabled}
                                        name={checkboxName}
                                        onChange={onChange}
                                        value={checkboxValue}
                                        {...CheckboxProps}
                                    />
                                }
                                key={label}
                                label={label}
                            />
                        );})}
                </FormGroup>
            </RootRef>
            <FormHelperText
                classes={{
                    root: defaultClasses.helperText
                }}
            >
                {helperText}
            </FormHelperText>
        </FormControl>
    );
};

CheckboxGroup.defaultProps = {
    CheckboxProps: {
        classes: {}
    },
    disabled:       false,
    FormGroupProps: {
        classes: {}
    },
    hidden:     false,
    inputProps: {},
    required:   false,
    value:      []
};

CheckboxGroup.propTypes = {
    CheckboxProps: shape({
        classes: shape({
            checkbox: string
        }).isRequired
    }),
    disabled:       bool,
    error:          oneOfType([object, string]),
    FormGroupProps: shape({
        classes: shape({
            root: string
        }).isRequired
    }),
    hidden:     bool,
    inputProps: object,
    label:      string,
    name:       string,
    options:    oneOfType([
        arrayOf(node),
        arrayOf(shape({
            key:   string,
            label: string
        }))
    ]),
    required: bool,
    value:    oneOfType([object, node])
};

export default CheckboxGroup;
